import * as echarts from 'echarts';
import { apiCalculate, CalcResult, IntersectionPoint } from 'scripts/api/calculate';

// Парсинг CSV формата: x,y,value
function parseCsvPoints(csv: string): { xAxis: string[], yAxis: string[], data: [number, number, number][] } {
    const lines = csv.trim().split('\n');
    const xSet = new Set<string>();
    const ySet = new Set<string>();
    const data: [number, number, number][] = [];

    let startIndex = 0;
    if (lines[0].toLowerCase().includes('x') && lines[0].toLowerCase().includes('y')) {
        startIndex = 1;
    }

    for (let i = startIndex; i < lines.length; i++) {
        const [x, y] = lines[i].split(',');
        xSet.add(x);
        ySet.add(y);
    }

    const xAxis = Array.from(xSet);
    const yAxis = Array.from(ySet);

    for (let i = startIndex; i < lines.length; i++) {
        const [x, y, value] = lines[i].split(',');
        const xIndex = xAxis.indexOf(x);
        const yIndex = yAxis.indexOf(y);
        data.push([xIndex, yIndex, Number(value)]);
    }

    return { xAxis, yAxis, data };
}

export async function drawHeatmap(sectorId: string, container: HTMLElement, csv: string) {
    const parsed = parseCsvPoints(csv);
    const chart = echarts.init(container);

    const pieces = [
        { gt: 0,   lte: 100, color: "#0879ff", label: "0 - 100" },
        { gt: 100, lte: 200, color: "#07fcff", label: "100 - 200" },
        { gt: 200, lte: 300, color: "#01ff84", label: "200 - 300" },
        { gt: 300, lte: 400, color: "#00ff01", label: "300 - 400" },
        { gt: 400, lte: 500, color: "#7eff00", label: "400 - 500" },
        { gt: 500, lte: 600, color: "#fff200", label: "500 - 600" },
        { gt: 600, lte: 700, color: "#ff7200", label: "600 - 700" },
        { gt: 700, lte: 800, color: "#ff0200", label: "700 - 800" },
        { gt: 800,            color: "#999999", label: "> 800" }
    ];

    const option: echarts.EChartsOption = {
        grid: { left: 20, right: 100, top: 40, bottom: 30, containLabel: true },
        tooltip: { position: 'top' },
        xAxis: {
            type: 'category',
            data: parsed.xAxis,
            name: 'φ',
            nameLocation: 'middle',
            nameGap: 28,
            axisLabel: {
                formatter: (val: string) => `${formatNumber(val)}°`
            }
        },
        yAxis: {
            type: 'category',
            data: parsed.yAxis,
            name: 'θ',
            nameLocation: 'middle',
            nameGap: 35,
            axisLabel: {
                formatter: (val: string) => `${formatNumber(val)}°`
            }
        },
        visualMap: {
            type: 'piecewise',
            pieces,
            orient: 'vertical',
            right: 0,
            top: 'middle',
            itemWidth: 12,
            itemHeight: 10,
            textStyle: { fontSize: 9 },
            calculable: true,
        },
        series: [{
            type: 'heatmap',
            data: parsed.data,
            label: { show: false },
            animation: false,
            itemStyle: { borderWidth: 0 },
            blurSize: 20,
            pointSize: 8
        }]
    };

    chart.setOption(option);
    attachTwoPointInteraction(chart, sectorId, container, parsed);
}


export async function drawHeatmapFlux(sectorId: string, container: HTMLElement, csv: string) {
    const parsed = parseCsvPoints(csv);

    // Автошкала для flux
    const positiveData = parsed.data.map(d => d[2]).filter(v => v > 0);
    const vmin = positiveData.length > 0 ? Math.min(...positiveData) : 1e-12;
    const vmax = positiveData.length > 0 ? Math.max(...positiveData) : 1.0;

    // Логарифмическая шкала уровней (10 цветов)
    const colors = [
        "#000000",
        "#630000",
        "#D10000",
        "#FF0000",
        "#FF9A00",
        "#adff00",
        "#00ffb6",
        "#6c006c",
        "#ffffff",
    ];

    // Генерируем границы для piecewise visualMap по логарифме
    const nColors = colors.length;
    const levels: number[] = [];
    for (let i = 0; i <= nColors; i++) {
        levels.push(vmin * Math.pow(vmax / vmin, i / nColors));
    }

    // Формируем кусочные элементы для visualMap
    const pieces: { gt: number; lte: number; color: string; label: string }[] = [];
    for (let i = 0; i < nColors; i++) {
        pieces.push({
            gt: levels[i],
            lte: levels[i + 1],
            color: colors[i],
            label: `${levels[i].toExponential(0)} - ${levels[i + 1].toExponential(0)}`
        });
    }

    const chart = echarts.init(container);

    const option: echarts.EChartsOption = {
        grid: {
            left: 20,
            right: 100,
            top: 40,
            bottom: 30,
            containLabel: true
        },
        tooltip: {
            position: 'top',
            formatter: (params: any) => {
                const val = params.value[2];
                return val === 0 ? '0' : val.toExponential(1);
            }
        },
       xAxis: {
            type: 'category',
            data: parsed.xAxis,
            name: 'φ',
            nameLocation: 'middle',
            nameGap: 28,
            axisLabel: {
                formatter: (val: string) => `${formatNumber(val)}°`
            }
        },
        yAxis: {
            type: 'category',
            data: parsed.yAxis,
            name: 'θ',
            nameLocation: 'middle',
            nameGap: 35,
            axisLabel: {
                formatter: (val: string) => `${formatNumber(val)}°`
            }
        },
        visualMap: {
            type: 'piecewise',
            pieces,
            orient: 'vertical',
            right: 0,
            top: 'middle',
            itemWidth: 12,
            itemHeight: 10,
            textStyle: { fontSize: 9 },
            calculable: true,
        },
        series: [{
            type: 'heatmap' as const,
            data: parsed.data,
            label: { show: false },
            animation: false,
            itemStyle: { borderWidth: 0 },

            blurSize: 20,
            pointSize: 8
        }]
    };

    chart.setOption(option);
    attachTwoPointInteraction(chart, sectorId, container, parsed);

    // Сохраняем координаты первой фиолетовой точки для тура
    chart.on('finished', function onFinished() {
        chart.off('finished', onFinished);
        const purpleGt = pieces[7].gt as number;
        const purpleLte = pieces[7].lte as number;
        const purplePoint = parsed.data.findLast(d => d[2] > purpleGt && d[2] <= purpleLte);
        if (purplePoint) {
            const pixel = chart.convertToPixel({ seriesIndex: 0 }, [purplePoint[0], purplePoint[1]]) as [number, number] | null;
            if (pixel) {
                container.dataset.tourHintX = String(Math.round(pixel[0]));
                container.dataset.tourHintY = String(Math.round(pixel[1]));
            }
        }
    });
}


function attachTwoPointInteraction(
    chart: echarts.ECharts,
    sectorId: string,
    container: HTMLElement,
    parsed: ReturnType<typeof parseCsvPoints>
) {
    let selectedPoints: [number, number][] = [];

    chart.on('click', (params: any) => {
        const [x, y] = params.value;

        if (selectedPoints.length < 2) {
            selectedPoints.push([x, y]);
        } else {
            selectedPoints = [[x, y]];
        }

        const highlightSeries: echarts.SeriesOption[] = [
            {
                type: 'scatter' as const,
                data: selectedPoints,
                symbolSize: 12,
                itemStyle: { color: '#FFFF00', borderColor: '#FF0000', borderWidth: 2 },
                z: 10
            }
        ];

        if (selectedPoints.length === 2) {
            highlightSeries.push({
                type: 'line',
                data: selectedPoints,
                lineStyle: { color: '#FF00FF', width: 2 },
                symbol: 'none',
                z: 5
            });

            // Передаём реальные значения из CSV, а не индексы ячеек
            const [xi1, yi1] = selectedPoints[0];
            const [xi2, yi2] = selectedPoints[1];
            const realFirst  = [Number(parsed.xAxis[xi1]), Number(parsed.yAxis[yi1])];
            const realSecond = [Number(parsed.xAxis[xi2]), Number(parsed.yAxis[yi2])];

            showThicknessResult(container, null);
            apiCalculate(sectorId, realFirst, realSecond)
                .then((result) => showThicknessResult(container, result))
                .catch(() => showThicknessResult(container, null, true));
        }

        chart.setOption({
            series: [
                {
                    type: 'heatmap' as const,
                    data: parsed.data,
                    label: { show: false },
                    animation: false,
                    itemStyle: { borderWidth: 0 }
                },
                ...highlightSeries
            ]
        });
    });
}

function showThicknessResult(container: HTMLElement, result: CalcResult | null, isError = false) {
    const BADGE_ID = 'thickness-result-badge';
    container.style.position = 'relative';

    let badge = container.querySelector(`#${BADGE_ID}`) as HTMLElement | null;

    if (!badge) {
        badge = document.createElement('div');
        badge.id = BADGE_ID;
        badge.style.cssText = `
            position: absolute;
            top: 8px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(20, 20, 30, 0.88);
            backdrop-filter: blur(6px);
            color: #fff;
            border-radius: 10px;
            padding: 8px 16px;
            font-size: 14px;
            font-family: sans-serif;
            display: flex;
            align-items: center;
            gap: 10px;
            z-index: 100;
            box-shadow: 0 4px 16px rgba(0,0,0,0.4);
            border: 1px solid rgba(255,255,255,0.12);
            white-space: nowrap;
            animation: fadeInDown 0.2s ease;
        `;

        // CSS-анимация (добавляем один раз в head)
        if (!document.getElementById('thickness-badge-style')) {
            const style = document.createElement('style');
            style.id = 'thickness-badge-style';
            style.textContent = `
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
                    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
                }
            `;
            document.head.appendChild(style);
        }

        const closeBtn = document.createElement('button');
closeBtn.innerHTML = '×';
closeBtn.style.cssText = `
    position: absolute;
    top: 6px;
    right: 8px;
    background: none;
    border: none;
    color: rgba(255,255,255,0.5);
    font-size: 18px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
`;
closeBtn.addEventListener('click', () => badge!.remove());

        const label = document.createElement('span');
        label.id = 'thickness-badge-label';

        badge.appendChild(label);
        badge.appendChild(closeBtn);
        container.appendChild(badge);
    }

    const label = badge.querySelector('#thickness-badge-label') as HTMLElement;

    if (isError) {
        badge.style.borderColor = 'rgba(255, 80, 80, 0.4)';
        label.innerHTML = `<span style="color:#ff6b6b;">⚠ Не удалось рассчитать</span>`;
        return;
    }

    if (result === null) {
        badge.style.borderColor = 'rgba(255,255,255,0.12)';
        label.innerHTML = `<span style="color:#aaa;">⏳ Вычисление...</span>`;
        return;
    }

    const intersectionInfo = (found: boolean, lbl: string) =>
        `<span style="font-size:11px; color:${found ? '#6dff9a' : '#ff6b6b'};">${found ? '✓' : '✗'} ${lbl}</span>`;

    const ENTRY_COLORS = ['#ff4f4f', '#ffa040', '#cc44ff', '#ff88cc', '#ffee44'];

    const intersectionsHtml = (pts: IntersectionPoint[]) => pts.map((pt, i) => {
        const color = ENTRY_COLORS[i % ENTRY_COLORS.length];
        return `
        <span style="display:flex; align-items:center; gap:6px;">
            <span style="width:8px; height:8px; border-radius:50%; background:${color}; flex-shrink:0;"></span>
            <span style="color:#8ecfff; font-size:11px;">Пересечение ${i + 1}</span>
            <span style="color:#555; margin:0 2px;">|</span>
            <span style="color:#aaa; font-size:11px;">толщина</span>
            <span style="font-size:15px; font-weight:600; color:#fff;">${pt.thickness.toFixed(1)}</span>
            <span style="color:#8ecfff; font-size:11px;">м</span>
            <span style="color:#555; margin:0 2px;">|</span>
            <span style="color:#aaa; font-size:11px;">дальность</span>
            <span style="font-size:14px; font-weight:500; color:#c8e6ff;">${pt.ray_length.toFixed(0)}</span>
            <span style="color:#8ecfff; font-size:11px;">м</span>
        </span>`;
    }).join('');

    badge.style.borderColor = result.entry1_found && result.entry2_found
        ? 'rgba(100, 200, 255, 0.3)'
        : 'rgba(255, 100, 100, 0.4)';

    const hasIntersections = result.intersections && result.intersections.length > 0;

    label.innerHTML = `
        <span style="display:flex; flex-direction:column; gap:4px;">
            <span style="display:flex; align-items:center; gap:8px;">
                ${intersectionInfo(result.entry1_found, 'луч 1')}
                ${intersectionInfo(result.entry2_found, 'луч 2')}
            </span>
            ${hasIntersections
                ? intersectionsHtml(result.intersections)
                : `<span style="color:#ff9966; font-size:12px;">Пересечение не найдено</span>`}
            ${result.plot_img ? `
            <span style="display:flex; align-items:center; justify-content:center; gap:10px; margin-top:2px;">
                <span style="cursor:pointer; color:#5ba4ff; font-size:11px;" id="toggle-plot-btn">▼ показать профиль</span>
                <span style="cursor:pointer; color:#5ba4ff; font-size:11px;" id="fullscreen-plot-btn">⛶ на весь экран</span>
            </span>
            <span id="plot-img-wrap" style="display:none;">
                <img src="data:image/png;base64,${result.plot_img}"
                     id="plot-img"
                     style="width:100%; max-width:520px; border-radius:6px; margin-top:4px; display:block; cursor:zoom-in;" />
            </span>` : ''}
        </span>
    `;

    // Разворачиваем/сворачиваем картинку
    const toggleBtn      = badge.querySelector('#toggle-plot-btn')   as HTMLElement | null;
    const fullscreenBtn  = badge.querySelector('#fullscreen-plot-btn') as HTMLElement | null;
    const plotWrap       = badge.querySelector('#plot-img-wrap')      as HTMLElement | null;
    const plotImg        = badge.querySelector('#plot-img')           as HTMLImageElement | null;

    toggleBtn?.addEventListener('click', () => {
        const hidden = plotWrap!.style.display === 'none';
        plotWrap!.style.display = hidden ? 'block' : 'none';
        toggleBtn.textContent   = hidden ? '▲ скрыть профиль' : '▼ показать профиль';
    });

    const openFullscreen = () => {
        if (!plotImg) return;
        openPlotFullscreen(plotImg.src);
    };

    fullscreenBtn?.addEventListener('click', openFullscreen);
    plotImg?.addEventListener('click', openFullscreen);
}

function openPlotFullscreen(src: string) {
    // Убираем предыдущий оверлей если есть
    document.getElementById('plot-fullscreen-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'plot-fullscreen-overlay';
    overlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.92);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2147483647;
        cursor: zoom-out;
        animation: fadeIn 0.15s ease;
    `;

    if (!document.getElementById('plot-fullscreen-style')) {
        const style = document.createElement('style');
        style.id = 'plot-fullscreen-style';
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; }
                to   { opacity: 1; }
            }
            #plot-fullscreen-overlay img {
                max-width: 95vw;
                max-height: 90vh;
                border-radius: 10px;
                box-shadow: 0 8px 48px rgba(0,0,0,0.8);
                cursor: default;
            }
            #plot-fullscreen-close {
                position: absolute;
                top: 18px;
                right: 24px;
                color: rgba(255,255,255,0.7);
                font-size: 32px;
                cursor: pointer;
                line-height: 1;
                user-select: none;
            }
            #plot-fullscreen-close:hover { color: #fff; }
        `;
        document.head.appendChild(style);
    }

    const img = document.createElement('img');
    img.src = src;
    img.addEventListener('click', e => e.stopPropagation());

    const closeBtn = document.createElement('span');
    closeBtn.id = 'plot-fullscreen-close';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => overlay.remove());

    overlay.appendChild(img);
    overlay.appendChild(closeBtn);
    overlay.addEventListener('click', () => overlay.remove());

    // Закрытие по Escape
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKey); }
    };
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
}

function formatNumber(val: any) {
    const num = Number(val);
    if (num === 0) return "0";

    // компактный формат
    if (Math.abs(num) < 1e-3 || Math.abs(num) > 1e4) {
        return num.toExponential(2); // 1.23e-5
    }

    return num.toFixed(2).replace(/\.?0+$/, ''); // убираем хвост нулей
}